import { useEffect, useState } from 'react';
import { ApiError, api, type Me } from '../lib/api';

/**
 * The one screen an unverified account sees.
 *
 * Sign-up asks for nothing beyond a name, an email and a password; this is the
 * price of that. Until the link in the email is followed, the API refuses
 * everything but this screen's three requests, so it says plainly what to do
 * and offers the only useful buttons: send it again, check again, sign out.
 */
export function VerifyEmail({
  me,
  onVerified,
  onSignOut,
}: {
  me: Me;
  onVerified: (me: Me) => void;
  onSignOut: () => void;
}) {
  const [busy, setBusy] = useState<'resend' | 'check' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('verifyError');
    if (error) {
      setNotice({ tone: 'error', text: error });
      params.delete('verifyError');
      const q = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : ''));
    }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /* Somebody who verified in another tab comes through without a reload. */
  useEffect(() => {
    const onFocus = () => {
      void api.me().then((next) => {
        if (next.user?.emailVerified) onVerified(next);
      }).catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [onVerified]);

  const resend = async () => {
    setBusy('resend');
    setNotice(null);
    try {
      await api.resendVerification();
      setNotice({ tone: 'ok', text: `Sent. Check ${me.user?.email} — and the spam folder, just in case.` });
      setCooldown(60);
    } catch (err) {
      setNotice({ tone: 'error', text: err instanceof ApiError ? err.message : 'Could not send it. Try again in a minute.' });
      if (err instanceof ApiError && err.status === 429) setCooldown(60);
    } finally {
      setBusy(null);
    }
  };

  const check = async () => {
    setBusy('check');
    try {
      const next = await api.me();
      if (next.user?.emailVerified) onVerified(next);
      else setNotice({ tone: 'error', text: 'Not confirmed yet. Open the link in the email, then try again.' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card verify-card">
        <div className="auth-brand">
          <img className="auth-wordmark" src="/brand/allrouted-wordmark.png" alt="AllRouted" width="466" height="72" />
        </div>

        <div className="verify-icon" aria-hidden="true">
          <svg viewBox="0 0 48 48" width="48" height="48">
            <rect x="6" y="11" width="36" height="26" rx="5" fill="none" stroke="currentColor" strokeWidth="2.4" />
            <path d="M8 14l16 12 16-12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
          </svg>
        </div>

        <h1 className="auth-title">Check your inbox</h1>
        <p className="verify-text">
          We sent a link to <strong>{me.user?.email}</strong>. Open it to confirm your email and your account opens
          straight away.
        </p>

        {notice ? (
          <p className={notice.tone === 'ok' ? 'verify-ok' : 'auth-error'} role="status">
            {notice.text}
          </p>
        ) : null}

        <button type="button" className="auth-submit" disabled={busy !== null} onClick={() => void check()}>
          {busy === 'check' ? 'Checking…' : 'I have confirmed it'}
        </button>
        <button
          type="button"
          className="auth-google"
          disabled={busy !== null || cooldown > 0}
          onClick={() => void resend()}
        >
          {busy === 'resend' ? 'Sending…' : cooldown > 0 ? `Send again in ${cooldown}s` : 'Send the email again'}
        </button>

        <p className="auth-alt">
          Wrong address?{' '}
          <button type="button" onClick={onSignOut}>
            Sign out
          </button>{' '}
          and sign up again, or write to support@allrouted.com.
        </p>
      </div>
    </div>
  );
}
